import type { ChainId } from '@morpho-org/blue-sdk'
import type { ApiInputToken, VaultRewards } from '@morpho-org/dapp-core'
import type { Address } from 'viem'

import type { VaultV2Action } from '@/lib/procedures/vaultV2Actions.contract'

import { toVaultV2ApyGross } from '@/helpers/vaultV2RateCap'
import {
  apiWarningLevelToEnum,
  isApiWarningLevel,
  type UnifiedVaultWarning
} from '@/helpers/warnings'

import type { VaultV2ApyData } from '../helpers/vaultV2Apy'

type Gate = { address: Address } | null

/**
 * What the vault V2 action panel, its operation hooks and the Bundler V2 recap read, built from
 * `vaultV2.actionPanel`. Gates keep the `{ address }` form `isVaultV2Gated` reads, and the asset
 * the `ApiInputToken` form `parseApiAsset` reads, with the price left null when Core has none.
 */
export interface VaultV2ActionPanelData {
  address: Address
  name: string
  image: string | null
  listed: boolean
  asset: ApiInputToken
  gates: {
    sendSharesGate: Gate
    receiveSharesGate: Gate
    sendAssetsGate: Gate
    receiveAssetsGate: Gate
  }
  warnings: UnifiedVaultWarning[]
  apy: number | null
  netApy: number | null
  netApyExcludingRewards: number | null
  apyBreakdown: VaultV2ApyData['apyBreakdown']
  maxApy: number | null
  /**
   * `0n` when the max rate is zero and null otherwise: the procedure exposes the max APY, not the
   * raw rate, and readers of `maxRate` only check for a zero cap.
   */
  maxRate: 0n | null
  performanceFee: number
  managementFee: number | null
  rewards: VaultRewards[]
}

const toGate = (address: Address | null): Gate => (address ? { address } : null)

/** An action whose spot APY may be missing, as `vaultV2.positionManager` returns it. */
type VaultV2ActionWithApy = Omit<VaultV2Action, 'apy'> & { apy: number | null }

/**
 * Null when Core reports no decimals or symbol for the vault asset: amounts can't be read or
 * formatted without them. A warning with a level the Vaults app doesn't know is dropped, since only red
 * warnings change what the panel allows. Also maps `vaultV2.positionManager`, whose `apy` is
 * null when its spot APY read failed.
 */
export function toVaultV2ActionPanelData<A extends VaultV2ActionWithApy>(
  action: A,
  chainId: ChainId,
  apy?: VaultV2ApyData
): (Omit<VaultV2ActionPanelData, 'apy'> & { apy: A['apy'] | null }) | null {
  const { asset, gates } = action
  if (asset.decimals === null || asset.symbol === null) return null

  return {
    address: action.address,
    name: action.name ?? '',
    image: action.image,
    listed: action.listed,
    asset: {
      address: asset.address,
      chain: { id: chainId },
      name: asset.name ?? asset.symbol,
      symbol: asset.symbol,
      decimals: asset.decimals,
      logoURI: asset.logoUri,
      priceUsd: asset.priceUsd,
      yield: asset.yieldApy === null ? null : { apy: asset.yieldApy },
      vault: asset.vault && {
        address: asset.vault.address,
        name: asset.vault.name ?? undefined,
        asset: { logoURI: asset.vault.assetLogoUri },
        state: { avgNetApy: asset.vault.netApy }
      }
    },
    gates: {
      sendSharesGate: toGate(gates.sendShares),
      receiveSharesGate: toGate(gates.receiveShares),
      sendAssetsGate: toGate(gates.sendAssets),
      receiveAssetsGate: toGate(gates.receiveAssets)
    },
    warnings: action.warnings.flatMap(({ type, level }) =>
      isApiWarningLevel(level) ? [{ type, level: apiWarningLevelToEnum(level) }] : []
    ),
    apy: apy ? toVaultV2ApyGross(apy) : action.apy,
    netApy: apy ? apy.netApy : action.netApy,
    netApyExcludingRewards: apy ? apy.netApyExcludingRewards : action.netApyExcludingRewards,
    apyBreakdown: apy?.apyBreakdown ?? null,
    maxApy: apy ? apy.maxRate : action.maxApy,
    maxRate: (apy ? apy.maxRate : action.maxApy) === 0 ? 0n : null,
    performanceFee: apy ? apy.fees.performanceFee : action.performanceFee,
    managementFee: apy ? apy.fees.managementFee : action.managementFee,
    rewards: (apy
      ? apy.rewards.map(({ token, supplyApr }) => ({
          token,
          supplyApr
        }))
      : action.rewards
    ).map(({ token, supplyApr }) => ({
      supplyApr,
      asset: {
        address: token.address,
        chain: { id: token.chainId },
        name: token.symbol ?? '',
        symbol: token.symbol ?? '',
        // A reward row shows its symbol, logo and APR; no amount is formatted with these decimals.
        decimals: 18,
        logoURI: token.logoUri
      }
    }))
  }
}
